/**
 * Behavioral Debrief Engine V2
 * ----------------------------
 * Deterministic post-simulation analysis. Consumes the trajectory
 * snapshots captured during a rehearsal and produces a full
 * behavioral dynamics report:
 *
 *   - pressure timeline (per-turn evolution)
 *   - detected countermoves (behavioral pivots)
 *   - response impact (posture → trajectory effect)
 *   - trajectory summary (cold operational assessment)
 *   - alternative path forecasts (counterfactual postures)
 *   - stabilization moments / escalation triggers
 *
 * No LLM calls. Pure tactical math driven by trajectory history.
 * Architecture is modular and extensible — future versions can
 * compose multi-session learning and benchmarking on top.
 */

import type { TrajectoryState, NextTurnStyle, PressureState } from "./behaviorRuntime";
import type { ForecastResponseMode } from "./forecastEngine";

// ─── Types ──────────────────────────────────────────────────────
export interface DebriefInput {
  trajectoryHistory: TrajectoryState[];
  responseModeHistory: ForecastResponseMode[];
}

export type Severity = "low" | "moderate" | "high";

export interface PressureTimelineEntry {
  turn: number;
  pressure: number;
  trend: "rising" | "stable" | "falling";
  delta: number;
  note: string;
  severity: Severity;
}

export interface DetectedCountermove {
  turn: number;
  style: NextTurnStyle;
  label: string;
  confidence: number; // 0–100
  severity: Severity;
}

export interface ResponseImpactRow {
  mode: ForecastResponseMode;
  modeLabel: string;
  turns: number;
  effects: { sign: "+" | "-"; label: string }[];
}

export interface AlternativeForecast {
  mode: ForecastResponseMode;
  modeLabel: string;
  projections: { sign: "+" | "-" | "→"; label: string }[];
  summary: string;
}

export interface StabilizationEvent {
  turn: number;
  type: "stabilization" | "escalation_trigger" | "reversal" | "volatility_spike";
  note: string;
  severity: Severity;
}

export interface BehaviorDebriefResult {
  pressureTimeline: PressureTimelineEntry[];
  detectedCountermoves: DetectedCountermove[];
  responseImpact: ResponseImpactRow[];
  trajectorySummary: string;
  alternativePathForecasts: AlternativeForecast[];
  stabilizationMoments: StabilizationEvent[];
  escalationTriggers: StabilizationEvent[];
  /** Aggregate metrics for future benchmarking */
  metrics: {
    avgPressure: number;
    peakPressure: number;
    avgVolatility: number;
    finalStability: number;
    dominantMode: ForecastResponseMode | null;
    pivotCount: number;
  };
}

// ─── Labels ─────────────────────────────────────────────────────
const STYLE_LABELS: Record<NextTurnStyle, string> = {
  victim_framing: "Victim Framing",
  passive_withdrawal: "Passive Withdrawal",
  shame_attack: "Shame Attack",
  subtle_minimization: "Subtle Minimization",
  guilt_reversal: "Guilt Pivot",
  silence_pressure: "Silence Pressure",
  tone_policing: "Tone Policing",
  emotional_exhaustion: "Emotional Exhaustion Pressure",
  reality_reversal: "Reality Reversal",
  coercive_pressure: "Coercive Pressure",
};

const STYLE_SEVERITY: Record<NextTurnStyle, Severity> = {
  reality_reversal: "high",
  coercive_pressure: "high",
  shame_attack: "high",
  guilt_reversal: "moderate",
  victim_framing: "moderate",
  emotional_exhaustion: "moderate",
  tone_policing: "moderate",
  subtle_minimization: "low",
  passive_withdrawal: "low",
  silence_pressure: "low",
};

const MODE_LABELS: Record<ForecastResponseMode, string> = {
  firm_boundary: "Firm Boundary",
  soft_neutral: "Soft Neutral",
  minimal_grey_rock: "Minimal Grey Rock",
};

// ─── Helpers ────────────────────────────────────────────────────
const sevFromPressure = (p: number): Severity =>
  p >= 70 ? "high" : p >= 45 ? "moderate" : "low";

const round = (n: number) => Math.round(n);
const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

// ─── Pressure Timeline ──────────────────────────────────────────
function buildPressureTimeline(history: TrajectoryState[]): PressureTimelineEntry[] {
  return history.map((s, i) => {
    const turn = i + 1;
    const trend = s.pressureTrend;
    const delta = s.pressureDelta;
    let note = "Pressure held within prior range.";

    if (s.pressureState === "emotional_reversal") {
      note = "Emotional reversal formed after sustained resistance.";
    } else if (delta >= 10) {
      note = "Pressure escalated sharply after exchange.";
    } else if (delta >= 5) {
      note = "Pressure escalated after defensive justification.";
    } else if (delta <= -10) {
      note = "Pressure collapsed — withdrawal trajectory formed.";
    } else if (delta <= -5) {
      note = "Conversation stability partially recovered.";
    } else if (s.withdrawalWeight >= 60) {
      note = "Withdrawal trajectory reinforced through restraint.";
    } else if (s.passiveAggressionWeight >= 65) {
      note = "Passive resistance signal strengthened.";
    } else if (s.deflectionRisk >= 65) {
      note = "Deflection probability elevated mid-exchange.";
    }

    return {
      turn,
      pressure: round(s.nextPressureLevel),
      trend,
      delta: round(delta),
      note,
      severity: sevFromPressure(s.nextPressureLevel),
    };
  });
}

// ─── Detected Countermoves ──────────────────────────────────────
function buildDetectedCountermoves(history: TrajectoryState[]): DetectedCountermove[] {
  const out: DetectedCountermove[] = [];
  let prevStyle: NextTurnStyle | null = null;
  history.forEach((s, i) => {
    const turn = i + 1;
    const isPivot = s.nextTurnStyle !== prevStyle;
    if (!isPivot) return;
    // Confidence weighted by pressure & escalation weight
    const conf = Math.min(
      100,
      40 + Math.round(s.escalationWeight * 0.35 + s.nextPressureLevel * 0.25),
    );
    out.push({
      turn,
      style: s.nextTurnStyle,
      label: STYLE_LABELS[s.nextTurnStyle],
      confidence: conf,
      severity: STYLE_SEVERITY[s.nextTurnStyle],
    });
    prevStyle = s.nextTurnStyle;
  });
  return out;
}

// ─── Response Impact ────────────────────────────────────────────
const MODE_EFFECTS: Record<ForecastResponseMode, { sign: "+" | "-"; label: string }[]> = {
  firm_boundary: [
    { sign: "+", label: "Reduced ambiguity" },
    { sign: "+", label: "Increased escalation pressure" },
    { sign: "-", label: "Lowered accountability probability" },
  ],
  minimal_grey_rock: [
    { sign: "+", label: "Reduced volatility" },
    { sign: "+", label: "Increased withdrawal drift" },
    { sign: "-", label: "Lowered conversational closure" },
  ],
  soft_neutral: [
    { sign: "+", label: "Stabilized emotional intensity" },
    { sign: "-", label: "Preserved conversational ambiguity" },
    { sign: "-", label: "Slowed accountability movement" },
  ],
};

function buildResponseImpact(modes: ForecastResponseMode[]): ResponseImpactRow[] {
  const counts: Record<ForecastResponseMode, number> = {
    firm_boundary: 0,
    soft_neutral: 0,
    minimal_grey_rock: 0,
  };
  modes.forEach((m) => (counts[m] = (counts[m] || 0) + 1));
  return (Object.keys(counts) as ForecastResponseMode[])
    .filter((m) => counts[m] > 0)
    .map((m) => ({
      mode: m,
      modeLabel: MODE_LABELS[m],
      turns: counts[m],
      effects: MODE_EFFECTS[m],
    }));
}

// ─── Trajectory Summary ─────────────────────────────────────────
function buildTrajectorySummary(history: TrajectoryState[], modes: ForecastResponseMode[]): string {
  if (history.length === 0) return "Insufficient signal for trajectory assessment.";
  const last = history[history.length - 1];
  const peak = Math.max(...history.map((s) => s.nextPressureLevel));
  const avgVol = avg(history.map((s) => s.volatility));
  const finalStab = last.conversationStability;
  const reversed = history.some((s) => s.pressureState === "emotional_reversal");
  const dominant = pickDominantMode(modes);

  if (reversed && finalStab < 45) return "Repeated emotional engagement amplified volatility.";
  if (last.atmosphere === "withdrawn" && dominant === "minimal_grey_rock")
    return "Withdrawal leverage replaced direct escalation pressure.";
  if (peak >= 70 && finalStab >= 50) return "Escalation stabilized but resistance remained active.";
  if (avgVol >= 55) return "Conversation destabilized through ambiguity preservation.";
  if (last.trajectoryDirection === "Pressure stabilizing") return "Pressure curve flattened under disengagement posture.";
  if (last.trajectoryDirection === "Escalation likely") return "Escalation trajectory persisted into final exchange.";
  return "Trajectory held within operational tolerance — no decisive resolution.";
}

function pickDominantMode(modes: ForecastResponseMode[]): ForecastResponseMode | null {
  if (!modes.length) return null;
  const counts: Record<ForecastResponseMode, number> = {
    firm_boundary: 0,
    soft_neutral: 0,
    minimal_grey_rock: 0,
  };
  modes.forEach((m) => counts[m]++);
  return (Object.keys(counts) as ForecastResponseMode[]).sort((a, b) => counts[b] - counts[a])[0];
}

// ─── Alternative Path Forecasts ─────────────────────────────────
function buildAlternativeForecasts(
  history: TrajectoryState[],
  modes: ForecastResponseMode[],
): AlternativeForecast[] {
  const dominant = pickDominantMode(modes);
  const last = history[history.length - 1];
  const peakPressure = history.length ? Math.max(...history.map((s) => s.nextPressureLevel)) : 50;

  const all: ForecastResponseMode[] = ["minimal_grey_rock", "firm_boundary", "soft_neutral"];
  return all
    .filter((m) => m !== dominant)
    .map((m) => {
      if (m === "minimal_grey_rock") {
        return {
          mode: m,
          modeLabel: MODE_LABELS[m],
          projections: [
            { sign: "+", label: "Withdrawal probability increases" },
            { sign: "-", label: "Escalation intensity decreases" },
            { sign: "→", label: "Disengagement trajectory accelerates" },
          ],
          summary:
            peakPressure >= 65
              ? "Likely defuses peak escalation but extends ambiguity."
              : "Reduces volatility — conversation drifts toward silence.",
        };
      }
      if (m === "firm_boundary") {
        return {
          mode: m,
          modeLabel: MODE_LABELS[m],
          projections: [
            { sign: "+", label: "Emotional resistance intensifies" },
            { sign: "-", label: "Ambiguity decreases" },
            { sign: "→", label: "Reversal probability rises" },
          ],
          summary:
            last && last.atmosphere === "volatile"
              ? "Likely accelerates collapse — high reversal risk."
              : "Forces tactical clarity at the cost of escalation pressure.",
        };
      }
      return {
        mode: m,
        modeLabel: MODE_LABELS[m],
        projections: [
          { sign: "+", label: "Short-term stability increases" },
          { sign: "-", label: "Long-term pressure persists" },
          { sign: "→", label: "Ambiguity preservation continues" },
        ],
        summary:
          "Defuses immediate volatility but preserves the underlying behavioral loop.",
      };
    });
}

// ─── Stabilization & Escalation Events ──────────────────────────
function buildEvents(history: TrajectoryState[]): {
  stabilizationMoments: StabilizationEvent[];
  escalationTriggers: StabilizationEvent[];
} {
  const stabilization: StabilizationEvent[] = [];
  const escalation: StabilizationEvent[] = [];

  history.forEach((s, i) => {
    const turn = i + 1;
    if (s.pressureDelta >= 8) {
      escalation.push({
        turn,
        type: "escalation_trigger",
        note: "Defensive justification increased pressure volatility.",
        severity: "high",
      });
    } else if (s.pressureDelta >= 4 && s.volatility >= 55) {
      escalation.push({
        turn,
        type: "volatility_spike",
        note: "Volatility spike — reactive engagement detected.",
        severity: "moderate",
      });
    }
    if (s.pressureDelta <= -6) {
      stabilization.push({
        turn,
        type: "stabilization",
        note: "Reduced engagement stabilized escalation trajectory.",
        severity: "low",
      });
    }
    if (s.pressureState === "emotional_reversal") {
      escalation.push({
        turn,
        type: "reversal",
        note: "Emotional reversal — agent shifted to victim framing.",
        severity: "high",
      });
    }
    if (s.pressureState === "leverage_withdrawal" && s.withdrawalWeight >= 55) {
      stabilization.push({
        turn,
        type: "stabilization",
        note: "Leverage withdrawal — escalation pressure replaced by silence.",
        severity: "moderate",
      });
    }
  });

  return { stabilizationMoments: stabilization, escalationTriggers: escalation };
}

// ─── Aggregate Metrics ──────────────────────────────────────────
function buildMetrics(
  history: TrajectoryState[],
  modes: ForecastResponseMode[],
  pivotCount: number,
) {
  if (!history.length) {
    return {
      avgPressure: 0,
      peakPressure: 0,
      avgVolatility: 0,
      finalStability: 0,
      dominantMode: pickDominantMode(modes),
      pivotCount,
    };
  }
  return {
    avgPressure: round(avg(history.map((s) => s.nextPressureLevel))),
    peakPressure: round(Math.max(...history.map((s) => s.nextPressureLevel))),
    avgVolatility: round(avg(history.map((s) => s.volatility))),
    finalStability: round(history[history.length - 1].conversationStability),
    dominantMode: pickDominantMode(modes),
    pivotCount,
  };
}

// ─── Public API ─────────────────────────────────────────────────
export function generateBehaviorDebrief(input: DebriefInput): BehaviorDebriefResult {
  const { trajectoryHistory: history, responseModeHistory: modes } = input;

  const pressureTimeline = buildPressureTimeline(history);
  const detectedCountermoves = buildDetectedCountermoves(history);
  const responseImpact = buildResponseImpact(modes);
  const trajectorySummary = buildTrajectorySummary(history, modes);
  const alternativePathForecasts = buildAlternativeForecasts(history, modes);
  const { stabilizationMoments, escalationTriggers } = buildEvents(history);
  const metrics = buildMetrics(history, modes, detectedCountermoves.length);

  return {
    pressureTimeline,
    detectedCountermoves,
    responseImpact,
    trajectorySummary,
    alternativePathForecasts,
    stabilizationMoments,
    escalationTriggers,
    metrics,
  };
}

// ─── Persistence helper (sessionStorage bridge) ─────────────────
const STORAGE_PREFIX = "greyrock_debrief_v2:";

export interface PersistedDebriefData {
  trajectoryHistory: TrajectoryState[];
  responseModeHistory: ForecastResponseMode[];
}

export function persistDebriefData(rehearsalId: string, data: PersistedDebriefData) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + rehearsalId, JSON.stringify(data));
  } catch {
    /* no-op */
  }
}

export function loadDebriefData(rehearsalId: string): PersistedDebriefData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + rehearsalId);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedDebriefData;
  } catch {
    return null;
  }
}
